import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Heart, Sparkles, Gift, Calendar } from 'lucide-react';
export function AboutPage() {
  return <div className="min-h-screen bg-white" data-id="element-1198">
      <Navbar data-id="element-1199" />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden bg-gradient-to-b from-rose-50/50 to-white" data-id="element-1200">
        {/* Decorative Background */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10" data-id="element-1201">
          <motion.div animate={{
          y: [0, -20, 0],
          rotate: [0, 5, 0]
        }} transition={{
          duration: 6,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="absolute top-20 left-[10%] w-64 h-64 bg-purple-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30" data-id="element-1202" />
          <motion.div animate={{
          y: [0, 30, 0],
          rotate: [0, -5, 0]
        }} transition={{
          duration: 7,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 1
        }} className="absolute top-40 right-[10%] w-72 h-72 bg-rose-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30" data-id="element-1203" />
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1204">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} data-id="element-1205">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-white border border-rose-100 shadow-sm mb-6" data-id="element-1206">
              <Heart className="w-4 h-4 text-rose-400 mr-2 fill-current" data-id="element-1207" />
              <span className="text-sm font-medium text-gray-600" data-id="element-1208">
                Our Story
              </span>
            </div>

            <h1 className="font-serif text-5xl lg:text-7xl font-bold text-gray-900 leading-tight mb-6" data-id="element-1209">
              Every Moment Deserves to Be{' '}
              <span className="text-rose-500" data-id="element-1210">Celebrated</span>
            </h1>

            <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto" data-id="element-1211">
              Bloom & Pop is a gift shop built on one simple belief: every
              moment deserves to be celebrated in a special way.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Journey Timeline */}
      <section className="py-20 bg-white" data-id="element-1212">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1213">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="text-center mb-16" data-id="element-1214">
            <h2 className="font-serif text-4xl font-bold text-gray-900 mb-4" data-id="element-1215">
              Our Journey
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-1216">
              From humble beginnings to a fresh vision focused on meaningful
              celebrations
            </p>
          </motion.div>

          <div className="relative" data-id="element-1217">
            {/* Timeline Line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gradient-to-b from-rose-200 via-rose-300 to-rose-200 hidden lg:block" data-id="element-1218" />

            {/* Timeline Items */}
            <div className="space-y-16" data-id="element-1219">
              {/* 2022 - The Beginning */}
              <motion.div initial={{
              opacity: 0,
              x: -50
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6
            }} className="relative grid lg:grid-cols-2 gap-8 items-center" data-id="element-1220">
                <div className="lg:text-right lg:pr-12" data-id="element-1221">
                  <div className="inline-block lg:block" data-id="element-1222">
                    <div className="inline-flex items-center px-4 py-2 rounded-full bg-rose-100 text-rose-600 font-bold mb-4" data-id="element-1223">
                      <Calendar className="w-4 h-4 mr-2" data-id="element-1224" />
                      2022
                    </div>
                    <h3 className="font-serif text-2xl font-bold text-gray-900 mb-3" data-id="element-1225">
                      The Beginning
                    </h3>
                    <p className="text-gray-600 leading-relaxed" data-id="element-1226">
                      Our journey began in Polokwane, Limpopo, started by a
                      young entrepreneur seeking an opportunity to grow in the
                      business world. Originally known as{' '}
                      <span className="font-medium text-gray-900" data-id="element-1227">
                        Ngaoto Media
                      </span>
                      , we took our first steps into the world of business.
                    </p>
                  </div>
                </div>
                <div className="lg:pl-12" data-id="element-1228">
                  <div className="relative rounded-2xl overflow-hidden shadow-xl aspect-[4/3]" data-id="element-1229">
                    <img src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&auto=format&fit=crop" alt="Entrepreneurship journey" className="w-full h-full object-cover" data-id="element-1230" />
                  </div>
                </div>
                {/* Timeline Dot */}
                <div className="hidden lg:block absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-6 h-6 bg-rose-500 rounded-full border-4 border-white shadow-lg z-10" data-id="element-1231" />
              </motion.div>

              {/* The Pause & Reflection */}
              <motion.div initial={{
              opacity: 0,
              x: 50
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.2
            }} className="relative grid lg:grid-cols-2 gap-8 items-center" data-id="element-1232">
                <div className="lg:col-start-2 lg:pl-12" data-id="element-1233">
                  <div className="inline-flex items-center px-4 py-2 rounded-full bg-purple-100 text-purple-600 font-bold mb-4" data-id="element-1234">
                    <Sparkles className="w-4 h-4 mr-2" data-id="element-1235" />
                    Reflection
                  </div>
                  <h3 className="font-serif text-2xl font-bold text-gray-900 mb-3" data-id="element-1236">
                    A Time to Grow
                  </h3>
                  <p className="text-gray-600 leading-relaxed" data-id="element-1237">
                    After some time of not operating, we took a step back to
                    reflect, learn, and reimagine what we truly wanted to
                    create. This pause became the foundation for something more
                    meaningful.
                  </p>
                </div>
                <div className="lg:col-start-1 lg:row-start-1 lg:pr-12" data-id="element-1238">
                  <div className="relative rounded-2xl overflow-hidden shadow-xl aspect-[4/3]" data-id="element-1239">
                    <img src="https://images.unsplash.com/photo-1499750310107-5fef28a66643?w=800&auto=format&fit=crop" alt="Planning and growth" className="w-full h-full object-cover" data-id="element-1240" />
                  </div>
                </div>
                {/* Timeline Dot */}
                <div className="hidden lg:block absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-6 h-6 bg-purple-500 rounded-full border-4 border-white shadow-lg z-10" data-id="element-1241" />
              </motion.div>

              {/* Today - Bloom & Pop */}
              <motion.div initial={{
              opacity: 0,
              x: -50
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.4
            }} className="relative grid lg:grid-cols-2 gap-8 items-center" data-id="element-1242">
                <div className="lg:text-right lg:pr-12" data-id="element-1243">
                  <div className="inline-block lg:block" data-id="element-1244">
                    <div className="inline-flex items-center px-4 py-2 rounded-full bg-rose-500 text-white font-bold mb-4" data-id="element-1245">
                      <Heart className="w-4 h-4 mr-2 fill-current" data-id="element-1246" />
                      Today
                    </div>
                    <h3 className="font-serif text-2xl font-bold text-gray-900 mb-3" data-id="element-1247">
                      Bloom & Pop is Born
                    </h3>
                    <p className="text-gray-600 leading-relaxed" data-id="element-1248">
                      A rebrand and a fresh start focused entirely on helping
                      people gift better and celebrate more meaningfully. Today,
                      we proudly introduce{' '}
                      <span className="font-bold text-rose-500" data-id="element-1249">
                        Bloom & Pop
                      </span>{' '}
                      — where every gift carries emotion, intention, and a
                      personal touch.
                    </p>
                  </div>
                </div>
                <div className="lg:pl-12" data-id="element-1250">
                  <div className="relative rounded-2xl overflow-hidden shadow-xl aspect-[4/3]" data-id="element-1251">
                    <img src="https://cdn.magicpatterns.com/uploads/tY5UdKvSqpLdqk7cARauxk/95b5029e17a0b637aa97170e10c3120c.jpg" alt="Bloom & Pop celebrations" className="w-full h-full object-cover" data-id="element-1252" />
                  </div>
                </div>
                {/* Timeline Dot */}
                <div className="hidden lg:block absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-6 h-6 bg-rose-500 rounded-full border-4 border-white shadow-lg z-10" data-id="element-1253">
                  <motion.div animate={{
                  scale: [1, 1.2, 1]
                }} transition={{
                  duration: 2,
                  repeat: Infinity
                }} className="absolute inset-0 bg-rose-500 rounded-full opacity-50" data-id="element-1254" />
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section className="py-20 bg-rose-50/30" data-id="element-1255">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1256">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="text-center mb-16" data-id="element-1257">
            <h2 className="font-serif text-4xl font-bold text-gray-900 mb-4" data-id="element-1258">
              What We Create
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-1259">
              We specialize in thoughtfully crafted gifts that create
              unforgettable moments
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-1260">
            {[{
            title: 'Balloons',
            description: 'Personalized balloons that float with joy and celebration',
            image: 'https://cdn.magicpatterns.com/uploads/27Mx1AbDkr24gNPNEm7K7U/R_(3).jpg',
            color: 'from-rose-400 to-rose-600'
          }, {
            title: 'Flowers',
            description: 'Fresh blooms arranged with care and artistry',
            image: 'https://cdn.magicpatterns.com/uploads/g4wvfJbAy29XT9uTzbmhYP/678a4c8333e128b4a59185410a4a6317.jpg',
            color: 'from-purple-400 to-purple-600'
          }, {
            title: 'Gift Hampers',
            description: 'Curated collections of treats and treasures',
            image: 'https://cdn.magicpatterns.com/uploads/6dsPw5GgJ3V5KCj9ERhNbL/2058a4df6a99f80acc98296ac4c55ac8.jpg',
            color: 'from-pink-400 to-pink-600'
          }, {
            title: 'Gift Boxes',
            description: 'Beautifully wrapped moments of surprise',
            image: 'https://cdn.magicpatterns.com/uploads/nK4NPPRs6gXWSuUU5PYpCz/20220207_110730-scaled.jpg',
            color: 'from-rose-400 to-rose-600'
          }].map((item, index) => <motion.div key={item.title} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} whileHover={{
            y: -8
          }} className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300" data-id="element-1261">
                <div className="relative h-48 overflow-hidden" data-id="element-1262">
                  <img src={item.image} alt={item.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" data-id="element-1263" />
                  <div className={`absolute inset-0 bg-gradient-to-t ${item.color} opacity-20 group-hover:opacity-30 transition-opacity`} data-id="element-1264" />
                </div>
                <div className="p-6" data-id="element-1265">
                  <h3 className="font-serif text-xl font-bold text-gray-900 mb-2" data-id="element-1266">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed" data-id="element-1267">
                    {item.description}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-20 bg-white" data-id="element-1268">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1269">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-1270">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} data-id="element-1271">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-rose-100 text-rose-600 font-medium mb-6" data-id="element-1272">
                <Gift className="w-4 h-4 mr-2" data-id="element-1273" />
                Our Mission
              </div>
              <h2 className="font-serif text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight" data-id="element-1274">
                You Celebrate —<br data-id="element-1275" />
                <span className="text-rose-500" data-id="element-1276">We Handle the Rest</span>
              </h2>
              <div className="space-y-4 text-lg text-gray-600 leading-relaxed" data-id="element-1277">
                <p data-id="element-1278">
                  We believe that a gift should be more than just an item — it
                  should carry{' '}
                  <span className="font-medium text-gray-900" data-id="element-1279">
                    emotion, intention, and a personal touch
                  </span>
                  .
                </p>
                <p data-id="element-1280">
                  That's why we focus on personalized gifts, where each order
                  becomes a message, a memory, and a meaningful expression of
                  love, appreciation, or celebration.
                </p>
                <p data-id="element-1281">
                  Whether you're celebrating a birthday, an anniversary, a
                  graduation, or any special milestone, we take care of the
                  details so you can focus on the moment and the people who
                  matter most.
                </p>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="relative" data-id="element-1282">
              <div className="grid grid-cols-2 gap-4" data-id="element-1283">
                <div className="space-y-4" data-id="element-1284">
                  <img src="https://cdn.magicpatterns.com/uploads/kt8QTDTbSkcFXKUnmKwZTA/3b153ec169318c392d15a5a952ea7f41.jpg" alt="Celebration moment" className="rounded-2xl shadow-lg w-full h-48 object-cover" data-id="element-1285" />
                  <img src="https://cdn.magicpatterns.com/uploads/bidiWptJqMDVMkPoktskPx/9b22454353ad10c0c0abd71c679c4fa0.jpg" alt="Gift giving" className="rounded-2xl shadow-lg w-full h-64 object-cover" data-id="element-1286" />
                </div>
                <div className="space-y-4 mt-8" data-id="element-1287">
                  <img src="https://cdn.magicpatterns.com/uploads/dXHPEt7Yw3pMAP9rY1R5yH/85771c431d647833304aa1099abe2f92.jpg" alt="Beautiful flowers" className="rounded-2xl shadow-lg w-full h-64 object-cover" data-id="element-1288" />
                  <img src="https://cdn.magicpatterns.com/uploads/ncZZZHYyeE6QJBbUXC8HwY/0c9e5cb8245286c06f035152cf25b89d.jpg" alt="Special moments" className="rounded-2xl shadow-lg w-full h-48 object-cover" data-id="element-1289" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Closing Statement */}
      <section className="py-20 bg-gradient-to-b from-rose-50/30 to-white" data-id="element-1290">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1291">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-1292">
            <h2 className="font-serif text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight" data-id="element-1293">
              With Bloom & Pop,
              <br data-id="element-1294" />
              Every Gift is Not Just Given —<br data-id="element-1295" />
              <span className="text-rose-500" data-id="element-1296">It's Felt</span>
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed mb-8" data-id="element-1297">
              Join us in celebrating life's beautiful moments, one thoughtful
              gift at a time.
            </p>
            <motion.div whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} data-id="element-1298">
              <a href="/" className="inline-flex items-center px-8 py-4 bg-rose-500 text-white rounded-full font-medium text-lg hover:bg-rose-600 transition-colors shadow-lg hover:shadow-xl" data-id="element-1299">
                Start Celebrating
                <Heart className="w-5 h-5 ml-2 fill-current" data-id="element-1300" />
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <Footer data-id="element-1301" />
    </div>;
}